/**
 * WuKong IM 全局连接插件
 *
 * @description 在用户登录后自动连接 WuKong IM，全局监听新消息
 *              复用 useWuKongIM composable 中的连接方法，避免代码重复
 *              支持监听登录状态，用户登录时自动连接
 * @author system
 * @date 2025-01-21
 */

import { watch } from 'vue'
// import { useWuKongIM } from '~/composables/chat/useWuKongIM'
import { useWuKongIMStore } from '~/stores/wukongIM'
import { useGlobalStore } from '~/stores/global'
import { useCustomerServiceStore } from '~/stores/customerService'

export default defineNuxtPlugin(async nuxtApp => {
  // 只在客户端执行
  if (!import.meta.client) return

  const { useWuKongIM } = await import('~/composables/chat/useWuKongIM')

  // 标记是否已获取最近会话
  let isLastConsultation = false
  // 标记是否已连接，避免重复连接
  let isConnected = false
  const route = useRoute()

  const getLastConsultation = async () => {
    const accessToken = useCookie('access_token')
    if (!accessToken.value) {
      return
    }
    if (isLastConsultation || route.fullPath.includes('/chat/service/')) {
      return
    }
    try {
      const res = await useHttp.post('/api/customer-service/consultation/service/userStats', {})
      if ((res.code === '1' || res.code === 1) && res.data) {
        const wukongIMStore = useWuKongIMStore()
        const customerServiceStore = useCustomerServiceStore()
        wukongIMStore.setUnreadCount(res.data.unReadCount || 0)
        wukongIMStore.updateLastMessage(res.data)
        // customerServiceStore.setConversationInfo(res.data)
        if (res.data.unReadCount > 0) {
          ElNotification({
            title: '💬 新消息',
            message: `<div style="cursor: pointer; display: flex; align-items: center; gap: 10px; padding: 4px 0;">
              <div style="width: 36px; height: 36px; border-radius: 50%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); display: flex; align-items: center; justify-content: center; color: #fff; font-size: 14px; font-weight: 600; flex-shrink: 0;">
                ${(res.data?.customerServiceInfo?.nickName || '客').slice(0, 1)}
              </div>
              <div style="flex: 1; min-width: 0;">
                <div style="font-size: 13px; color: #909399;">给您发来了一条新消息，点击查看</div>
              </div>
              <div style="color: #1890ff; font-size: 20px;">›</div>
            </div>`,
            position: 'bottom-right',
            duration: 4000,
            dangerouslyUseHTMLString: true,
            onClick: () => {
              const { openService: openCustomerService } = useCustomerService()
              openCustomerService()
            }
          })
        }
      }
      isLastConsultation = true
    } catch (error) {
      console.error('获取最近会话失败:', error)
    }
  }

  /**
   * 检查并自动连接
   */
  const checkAndConnect = async () => {
    console.log('🔌 [WuKongIM Plugin] checkAndConnect 开始执行')

    // 如果已连接，跳过
    if (isConnected) {
      console.log('👤 [WuKongIM Plugin] 已连接，跳过重复连接')
      return
    }

    const globalStore = useGlobalStore()
    // 检查用户是否已登录
    const accessToken = useCookie('access_token')
    console.log('🔌 [WuKongIM Plugin] access_token:', accessToken.value ? '存在' : '不存在')

    if (!accessToken.value) {
      console.log('👤 [WuKongIM Plugin] 用户未登录，跳过连接')
      return
    }

    // 使用 globalStore.user 获取用户信息
    let userId = globalStore.user?.id || globalStore.user?.userId
    console.log('🔌 [WuKongIM Plugin] 当前 userId:', userId)

    if (!userId) {
      try {
        console.log('🔌 [WuKongIM Plugin] 正在获取用户信息...')
        await globalStore.getUser()
        userId = globalStore.user?.id || globalStore.user?.userId
        console.log('🔌 [WuKongIM Plugin] 获取用户信息后 userId:', userId)
      } catch (error) {
        console.error('🔌 [WuKongIM Plugin] 获取用户信息失败:', error)
        return
      }
    }

    if (userId && accessToken.value) {
      console.log('🔌 [WuKongIM Plugin] 准备连接 WuKong IM, userId:', userId)
      // 复用 composable 的连接方法
      const { connect } = useWuKongIM()
      try {
        await connect(String(userId), accessToken.value)
        isConnected = true
      } catch (error) {
        console.error('🔌 [WuKongIM Plugin] WuKong IM 连接失败:', error)
      }
    } else {
      console.log('🔌 [WuKongIM Plugin] 缺少 userId 或 token，无法连接')
    }
  }

  /**
   * 监听登录状态变化
   */
  const watchLoginStatus = () => {
    const globalStore = useGlobalStore()

    watch(
      () => globalStore.isLogin,
      async (newVal, oldVal) => {
        if (newVal && !oldVal) {
          // 用户刚刚登录，执行连接
          console.log('👤 检测到用户登录，准备连接 WuKong IM')
          await new Promise(resolve => setTimeout(resolve, 200))
          await checkAndConnect()
          await getLastConsultation()
        } else if (!newVal && oldVal) {
          // 用户登出，断开连接
          console.log('👤 检测到用户登出，断开 WuKong IM 连接')
          const { disconnect } = useWuKongIM()
          disconnect()
          isConnected = false
          isLastConsultation = false
        }
      },
      { immediate: false }
    )
  }

  // 应用初始化完成后检查连接
  nuxtApp.hook('app:mounted', () => {
    console.log('🔌 [WuKongIM Plugin] app:mounted 钩子触发')
    // 延迟一点执行，确保 store 已初始化
    setTimeout(() => {
      console.log('🔌 [WuKongIM Plugin] 延迟执行开始')
      // 初始化时检查连接（处理页面刷新时已登录的情况）
      checkAndConnect()
      getLastConsultation()
      // 开始监听登录状态变化
      watchLoginStatus()
    }, 1500)
  })

  // 提供全局方法
  return {
    provide: {
      wukongIM: {
        connect: async (userId: string, token: string) => {
          const { connect } = useWuKongIM()
          await connect(userId, token)
          isConnected = true
        },
        disconnect: () => {
          const { disconnect } = useWuKongIM()
          disconnect()
          isConnected = false
        },
        checkAndConnect
      }
    }
  }
})
